import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import { FormControl, InputLabel, Select, MenuItem } from "@material-ui/core";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import TagModal from "../../components/TagModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Chip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { MoreHoriz } from "@material-ui/icons";
import ContactTagListModal from "../../components/ContactTagListModal";

// ── Reducer compartilhado ─────────────────────────────────────────────────────
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_TAGS":
      return [...state, ...action.payload];
    case "UPDATE_TAGS": {
      const tag = action.payload;
      const idx = state.findIndex((s) => s.id === tag.id);
      if (idx !== -1) {
        const next = [...state];
        next[idx] = tag;
        return next;
      }
      return [tag, ...state];
    }
    case "DELETE_TAGS":
      return state.filter((tag) => tag.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  tabs: {
    backgroundColor: theme.mode === "light" ? "#f2f2f2" : "#7f7f7f",
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
}));

// ── Sub-componente de lista de tags ───────────────────────────────────────────
function TagList({ kanban, socket, user }) {
  const classes = useStyles();

  const [selectedTagContacts, setSelectedTagContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [selectedTagName, setSelectedTagName] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [deletingTag, setDeletingTag] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [tags, dispatch] = useReducer(reducer, []);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const pageNumberRef = useRef(1);

  // Fetch tags
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    pageNumberRef.current = 1;
  }, [searchParam, kanban]);

  useEffect(() => {
    const delay = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/tags/", {
          params: { searchParam, pageNumber, kanban, limit: pageSize },
        });
        dispatch({ type: "LOAD_TAGS", payload: data.tags });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber, pageSize, kanban]);

  // Socket
  useEffect(() => {
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_TAGS", payload: data.tag });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TAGS", payload: +data.tagId });
      }
    };
    socket.on(`company${user.companyId}-tag`, onEvent);
    return () => socket.off(`company${user.companyId}-tag`, onEvent);
  }, [socket, user.companyId]);

  const handleSearch = (e) => {
    setSearchParam(e.target.value.toLowerCase());
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPageNumber(1);
    dispatch({ type: "RESET" });
  };

  const handleDeleteTag = async (tagId) => {
    try {
      await api.delete(`/tags/${tagId}`);
      toast.success(i18n.t("tags.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingTag(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  return (
    <>
      <ConfirmationModal
        title={deletingTag ? i18n.t("tags.confirmationModal.deleteTitle") : ""}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteTag(deletingTag.id)}
      >
        {i18n.t("tags.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      {contactModalOpen && kanban === 0 && (
        <ContactTagListModal
          open={contactModalOpen}
          onClose={() => {
            setContactModalOpen(false);
            setSelectedTagContacts([]);
            setSelectedTagName("");
          }}
          tag={selectedTagName}
        />
      )}

      <TagModal
        open={tagModalOpen}
        onClose={() => { setSelectedTag(null); setTagModalOpen(false); }}
        aria-labelledby="form-dialog-title"
        tagId={selectedTag && selectedTag.id}
        kanban={kanban}
      />

      {/* Barra de ação */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel id="page-size-label">Itens/página</InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Itens/página"
          >
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
        <TextField
          placeholder={i18n.t("contacts.searchPlaceholder")}
          type="search"
          value={searchParam}
          onChange={handleSearch}
          size="small"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ color: "gray" }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => { setSelectedTag(null); setTagModalOpen(true); }}
        >
          {i18n.t("tags.buttons.add")}
        </Button>
      </div>

      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {kanban === 0 && (
                <TableCell align="center">{i18n.t("tags.table.id")}</TableCell>
              )}
              <TableCell align="center">{i18n.t("tags.table.name")}</TableCell>
              <TableCell align="center">
                {kanban === 0
                  ? i18n.t("tags.table.contacts")
                  : i18n.t("tagsKanban.table.tickets")}
              </TableCell>
              <TableCell align="center">{i18n.t("tags.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                {kanban === 0 && (
                  <TableCell align="center">{tag.id}</TableCell>
                )}
                <TableCell align="center">
                  <Chip
                    variant="outlined"
                    style={{
                      backgroundColor: tag.color,
                      textShadow: "1px 1px #000",
                      color: "white",
                    }}
                    label={tag.name}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {kanban === 0 ? (
                    <>
                      {tag?.contacts?.length}
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedTagContacts(tag?.contacts);
                          setContactModalOpen(true);
                          setSelectedTagName(tag);
                        }}
                        disabled={!tag?.contacts?.length}
                      >
                        <MoreHoriz />
                      </IconButton>
                    </>
                  ) : (
                    <span>{tag?.ticketTags ? tag.ticketTags.length : 0}</span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setSelectedTag(tag); setTagModalOpen(true); }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => { setConfirmModalOpen(true); setDeletingTag(tag); }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {loading && Array.from({ length: 5 }).map((_, idx) => (
              <TableRowSkeleton key={`skeleton-${idx}`} columns={kanban === 0 ? 4 : 3} />
            ))}
          </TableBody>
        </Table>

        {hasMore && (
          <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
            <Button variant="outlined" color="primary" onClick={loadMore} disabled={loading}>
              {loading ? i18n.t("contacts.loading") : i18n.t("contacts.loadMore")}
            </Button>
          </div>
        )}
      </Paper>
    </>
  );
}

// ── Página principal com abas ─────────────────────────────────────────────────
const Tags = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);

  return (
    <MainContainer>
      <MainHeader>
        <Title>{activeTab === 0 ? i18n.t("tags.title") : i18n.t("tagsKanban.title")}</Title>
        <MainHeaderButtonsWrapper />
      </MainHeader>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Tags de Tickets" />
        <Tab label="Tags do Kanban" />
      </Tabs>

      {activeTab === 0 && (
        <TagList kanban={0} socket={socket} user={user} />
      )}
      {activeTab === 1 && (
        <TagList kanban={1} socket={socket} user={user} />
      )}
    </MainContainer>
  );
};

export default Tags;
